import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { isBranchFunction, type FunctionSuggestion } from "../utils";

/**
 * Podgląd sugestii funkcji słupów wyliczonych z topologii rysunku.
 * NIC nie zmienia się bez zatwierdzenia: checkboxy per wiersz (domyślnie tylko
 * różniące się), „Zastosuj wybrane" nanosi funkcje na rysunek (dirty).
 *
 * Słup z już wpisaną funkcją rozgałęźną NIGDY nie jest zaznaczony domyślnie:
 * wariant R** (RPP/RPK/RNK) zależy od zakotwienia odgałęzienia, którego nie
 * widać na rysunku, więc decyzja projektanta ma pierwszeństwo przed heurystyką.
 */
export function FunctionSuggestionsModal({
  suggestions,
  onApply,
  onClose,
}: {
  suggestions: FunctionSuggestion[];
  /** ref → suggested function, only for the checked rows. */
  onApply: (updates: Record<string, string>) => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const s of suggestions) {
      init[s.ref] =
        s.suggested != null && s.suggested !== s.current && !isBranchFunction(s.current);
    }
    return init;
  });

  const selectable = suggestions.filter((s) => s.suggested != null);
  const selectedCount = selectable.filter((s) => checked[s.ref]).length;
  const allSelected = selectable.length > 0 && selectedCount === selectable.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = (value: boolean) => {
    setChecked((c) => {
      const next = { ...c };
      for (const s of selectable) next[s.ref] = value;
      return next;
    });
  };

  const apply = () => {
    const updates: Record<string, string> = {};
    for (const s of suggestions) {
      if (s.suggested != null && checked[s.ref]) updates[s.ref] = s.suggested;
    }
    onApply(updates);
  };

  return (
    <Modal open onClose={onClose} title="Sugestie funkcji słupów" size="lg">
      <p className="mb-3 text-[11px] text-[var(--color-text-secondary)]">
        Sugestie wyliczone z topologii rysunku. Odgałęzienie (linia od słupa mająca co
        najmniej 4 żyły o przekroju od 35 mm²) czyni słup rozgałęźnym — proponujemy wtedy
        RPK, bo tego, czy odgałęzienie jest zakotwione, nie widać na rysunku; RPP i RNK
        wybierz ręcznie. Cieńsze linie to przyłącza i nie zmieniają funkcji. Zmienią się
        tylko zaznaczone wiersze — słup z już wpisaną funkcją rozgałęźną nie jest
        zaznaczany, zaznacz go sam, jeśli chcesz nadpisać.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)] text-left">
            <th className="w-8 px-2 py-1.5">
              <input
                type="checkbox"
                aria-label="Zaznacz wszystkie słupy"
                title="Zaznacz / odznacz wszystkie słupy z sugestią"
                disabled={selectable.length === 0}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={(e) => toggleAll(e.target.checked)}
              />
            </th>
            <th className="section-label px-2 py-1.5">Słup</th>
            <th className="section-label px-2 py-1.5">Funkcja obecna</th>
            <th className="section-label px-2 py-1.5">Sugerowana</th>
          </tr>
        </thead>
        <tbody>
          {suggestions.map((s) => {
            const differs = s.suggested != null && s.suggested !== s.current;
            // Projektant już zdecydował, że słup jest rozgałęźny — nie podświetlamy
            // tego jako rozbieżności do poprawienia.
            const keepsCurrent = differs && isBranchFunction(s.current);
            return (
              <tr
                key={s.ref}
                className={`border-b border-[var(--color-border-muted)] last:border-0 ${
                  differs && !keepsCurrent ? "bg-amber-500/10" : ""
                }`}
              >
                <td className="px-2 py-1.5">
                  <input
                    type="checkbox"
                    disabled={s.suggested == null}
                    checked={s.suggested != null && !!checked[s.ref]}
                    onChange={(e) =>
                      setChecked((c) => ({ ...c, [s.ref]: e.target.checked }))
                    }
                  />
                </td>
                <td className="px-2 py-1.5 font-mono text-xs font-bold">{s.ref}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{s.current || "—"}</td>
                <td
                  className={`px-2 py-1.5 font-mono text-xs ${
                    differs && !keepsCurrent ? "font-bold text-amber-600" : ""
                  } ${keepsCurrent ? "text-[var(--color-text-secondary)]" : ""}`}
                >
                  {s.suggested ?? "— brak sugestii (słup bez przęseł) —"}
                  {keepsCurrent && (
                    <span className="ml-2 font-sans text-[11px]">
                      (zostawiamy Twoją funkcję rozgałęźną)
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Anuluj
        </Button>
        <Button size="sm" disabled={selectedCount === 0} onClick={apply}>
          Zastosuj wybrane ({selectedCount})
        </Button>
      </div>
    </Modal>
  );
}
